import * as React from "react";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { theme } from "styles/theme";
import {
  LEAD_TIME_MINUTES_MAX,
  LEAD_TIME_SEGMENT_MAX,
  normalizeLeadTimeDigits,
} from "views/vendors/constants/vendorLeadTime";

type Props = {
  name?: string;
  value?: string;
  onValueChange: (digits: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
};

const StyledInput = styled.input`
  outline: none;
  font-weight: 500;
  border: 1px solid ${theme.border.light};
  border-radius: 5px;
  background: white;
  margin: 0;
  height: ${theme.height.regular};
  padding: 0 0.5em;
  transition: all 0.09s linear;
  font-size: ${theme.fontSize.regular};
  font-variant-numeric: tabular-nums;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  letter-spacing: 0.02em;
  &:disabled {
    background: ${theme.bg.blue};
    cursor: not-allowed;
  }
  &:hover {
    border: 1px solid #5d6d7e;
  }
  &:focus {
    background: transparent;
    border: 1px solid #3498db;
  }
`;

/** e.g. 021430 → 02d : 14h : 30m */
export const formatLeadTimeDigitsDisplay = (digits: string): string => {
  const filled = digits.padStart(6, "0");
  const days = filled.slice(0, 2);
  const hours = filled.slice(2, 4);
  const minutes = filled.slice(4, 6);
  return `${days}d : ${hours}h : ${minutes}m`;
};

const isValid = (digits: string): boolean => {
  const cleaned = digits.replace(/[^0-9]/g, "");
  if (!cleaned) return true;
  if (cleaned.length > 6) return false;
  if (cleaned.length < 6) return true;

  const padded = cleaned.padStart(6, "0").slice(-6);
  const days = parseInt(padded.slice(0, 2), 10);
  const hours = parseInt(padded.slice(2, 4), 10);
  const minutes = parseInt(padded.slice(4, 6), 10);
  return (
    Number.isFinite(days) &&
    days <= LEAD_TIME_SEGMENT_MAX &&
    Number.isFinite(hours) &&
    hours <= LEAD_TIME_SEGMENT_MAX &&
    Number.isFinite(minutes) &&
    minutes <= LEAD_TIME_SEGMENT_MAX
  );
};

export default function MaskedLeadTimeInput({
  value = "",
  onValueChange,
  placeholder = "00d : 00h : 00m",
  onBlur,
  onFocus,
  ...rest
}: Props) {
  const externalDigits = value.replace(/[^0-9]/g, "").slice(-6);
  const [digits, setDigits] = useState(externalDigits);
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDigits(externalDigits);
    }
  }, [externalDigits]);

  const commitDigits = (next: string) => {
    setDigits(next);
    onValueChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      if (digits.length === 0) return;
      commitDigits(digits.slice(0, -1));
      return;
    }
    if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      const next = (digits + e.key).slice(-6);
      if (!isValid(next)) return;
      commitDigits(next);
      return;
    }
    const allow = [
      "Tab",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Enter",
      "Escape",
    ];
    if (!allow.includes(e.key) && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    const next = (digits + pasted).slice(-6);
    if (!isValid(next)) return;
    commitDigits(next);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = true;
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;
    const normalized = normalizeLeadTimeDigits(digits);
    if (normalized !== digits) {
      setDigits(normalized);
      onValueChange(normalized);
    }
    onBlur?.(e);
  };

  return (
    <StyledInput
      type="text"
      inputMode="numeric"
      value={formatLeadTimeDigitsDisplay(digits)}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChange={() => {}}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      {...rest}
    />
  );
}
